import { loadFromStorage, saveToStorage } from '@/utils/client-storage/localStorage';
import { loadFromSessionStorage } from '@/utils/client-storage/sessionStorage';
import { getCookie, shouldIncludeScreenName } from '@/utils/functions';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import secureApi from './secureApi';
// LogRocket is loaded dynamically on user interaction, access via window

interface ClaimPayload {
  claim_number?: string;
  [key: string]: any;
}

interface HandleClaimUpsertData {
  claimNumber?: string;
  sessionStart?: string;
  [key: string]: any;
}

export const claimUpsert = async (data: HandleClaimUpsertData) => {
  const resolvedClaimNumber =
    data?.claimNumber ||
    loadFromStorage('currentId') ||
    loadFromSessionStorage('currentId') ||
    getCookie('currentId');
  const claimCreationDate = loadFromStorage('claimCreationDate');
  console.log('------resolvedClaimNumber: ', resolvedClaimNumber);

  if (data?.claimNumber) {
    saveToStorage('currentId', data?.claimNumber);
  }

  try {
    const screen_name = `${window.location.hostname}${window.location?.pathname}`;
    const upsertClaimUrl = '/session/claim';
    const sessionStart = data?.sessionStart || loadFromStorage('sessionStart');

    let payload: ClaimPayload = {
      claim_number: resolvedClaimNumber,
      ...data,
    };

    if (!screen_name.includes('manual-lender')) {
      payload = {
        ...payload,
        screen_name: screen_name,
        should_add_screen_name: shouldIncludeScreenName(screen_name),
      };
    }

    if (sessionStart) {
      const sessionEnd = new Date().getTime();
      const duration = sessionEnd - parseInt(sessionStart, 10);
      payload.session_start = sessionStart;
      payload.session_duration = duration;
    }

    const LogRocket = (window as any).LogRocket;
    const sessionRecordingUrl =
      typeof LogRocket?.sessionURL === 'string' && /^https?:\/\/.+/.test(LogRocket.sessionURL)
        ? LogRocket.sessionURL
        : null;
    if (sessionRecordingUrl) payload.session_recording_url = sessionRecordingUrl;

    const response = await secureApi(upsertClaimUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      const error = new Error(errorData.message || `HTTP error! Status: ${response.status}`);
      (error as any).response = { status: response.status, data: errorData };
      throw error;
    }
    const responseData = await response.json();
    if (!claimCreationDate) {
      saveToStorage('claimCreationDate', responseData?.responseObject?.createdAt);
    }

    return responseData;
  } catch (e: any) {
    console.error(e?.message || 'Error upserting claim!');
    throw e;
  }
};

export const useClaimUpsert = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, HandleClaimUpsertData, unknown>({
    mutationFn: claimUpsert,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['claim', variables.claimNumber] });
    },
  });
};
